import type { Event } from '@patsnap/tahiti-core';
import type { FeatureRecognizeResult } from './feature-identify';
import type { RetrievalElement, ExtendRetrievalElementResponse } from '../apis/retrieval-elements-api';

/**
 * 原始输入获取服务
 * @description 用于获取原始输入，并更新到模型 {@link RetrievalResultReportModel}
 */
export interface OriginalInputService {
    /**
     * 获取原始输入，并更新到模型 {@link RetrievalResultReportModel}
     * @returns 原始输入
     */
    pullOriginalInput(): Promise<string>;
}

export const OriginalInputService = Symbol.for('OriginalInputService');

/**
 * 获取技术特征服务
 * @description 用于获取技术特征，并更新到模型 {@link RetrievalResultReportModel}
 */
export interface TechFeaturesService {
    /**
     * 获取技术特征
     * @returns 技术特征
     */
    pullTechFeatures(): Promise<FeatureRecognizeResult>;
}

export const TechFeaturesService = Symbol.for('TechFeaturesService');

/**
 * 新颖性报告名称服务
 */
export interface NoveltyCheckReportTitleService {
    /**
     * 获取新颖性报告名称，并更新到模型 {@link RetrievalResultReportModel}
     * @returns 新颖性报告名称
     */
    pullNoveltyCheckReportTitle(): Promise<string>;
}

export const NoveltyCheckReportTitleService = Symbol.for('NoveltyCheckReportTitleService');

/**
 * 评述报告服务
 */
export interface NoveltyReviewService {
    /**
     * 评述报告加载事件
     */
    onNoveltyReviewLoading: Event<boolean>;
    /**
     * 获取评述报告，并更新到模型 {@link RetrievalResultReportModel}
     * @returns 评述报告
     */
    pullNoveltyReview(): Promise<void>;
}

export const NoveltyReviewService = Symbol.for('NoveltyReviewService');

/**
 * 对比文献公开情况服务
 */
export interface ComparativeLiteraturePublicityService {
    /**
     * 对比文献公开情况加载事件
     */
    onComparativeLiteraturePublicityLoading: Event<boolean>;
    /**
     * 获取对比文献公开情况
     * @returns 对比文献公开情况
     */
    pullComparativeLiteraturePublicity(): Promise<void>;
}

export const ComparativeLiteraturePublicityService = Symbol.for('ComparativeLiteraturePublicityService');

/**
 * 技术要点更新服务
 */
export interface TechEssentialService {
    /**
     * 获取技术要点
     * @returns 技术要点
     */
    pullTechEssential(): Promise<string>;
    /**
     * 更新技术要点
     * @param techEssential 技术要点
     */
    updateTechEssential(techEssential: string): Promise<void>;
}

export const TechEssentialService = Symbol.for('TechEssentialService');

/**
 * 检索结果报告副标题信息服务
 */
export interface RetrievalResultReportSubtitleInfoService {
    /**
     * 获取检索结果报告副标题信息
     */
    pullRetrievalResultReportSubtitleInfo(): Promise<void>;
}

export const RetrievalResultReportSubtitleInfoService = Symbol.for('RetrievalResultReportSubtitleInfoService');

/**
 * 检索要素服务接口
 */
export const RetrievalElementsService = Symbol.for('RetrievalElementsService');

export interface RetrievalElementsService {
    /**
     * 获取检索要素列表
     * @param taskId 任务ID
     * @returns 检索要素列表
     */
    pullRetrievalElements(): Promise<RetrievalElement[]>;

    /**
     * 更新检索要素列表
     * @param elements 检索要素列表
     * @param familyMergeEnabled 是否启用同族合并
     * @returns 是否更新成功
     */
    updateRetrievalElements(elements: RetrievalElement[], familyMergeEnabled?: boolean): Promise<boolean>;

    /**
     * 扩展单个检索要素
     * @param word 需要扩展的文本
     * @returns 扩展结果
     */
    extendRetrievalElement(word: string): Promise<ExtendRetrievalElementResponse>;

    /**
     * 检索要素加载状态
     */
    readonly isElementsLoading: boolean;

    /**
     * 检索要素列表
     */
    readonly elements: RetrievalElement[];

    /**
     * 检索要素变更事件
     */
    readonly onElementsChange: Event<RetrievalElement[]>;

    /**
     * 加载状态变更事件
     */
    readonly onLoadingChange: Event<boolean>;
}
